import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Save, Plus, Trash2, ChevronUp, ChevronDown, ChevronRight,
  RotateCcw, Sparkles, Layers, Bot, Shield, Star, Palette,
  ShoppingBag, Calendar, Video, PenLine, Newspaper, Zap, Globe, Cpu, Mail,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = ['Layers', 'Bot', 'Shield', 'Star', 'Palette', 'ShoppingBag', 'Calendar', 'Video', 'PenLine', 'Newspaper', 'Sparkles', 'Zap', 'Globe', 'Cpu', 'Mail'];
const ICON_LOOKUP = { Layers, Bot, Shield, Star, Palette, ShoppingBag, Calendar, Video, PenLine, Newspaper, Sparkles, Zap, Globe, Cpu, Mail };

/* ---------- Reusable atoms ---------- */
function Field({ label, children, hint, full }) {
  return (
    <div className={full ? 'sm:col-span-full' : ''}>
      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-slate-600 mt-1">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, placeholder, accent, className = '' }) {
  return (
    <input
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-white/5 border rounded-lg px-3 py-2 text-sm focus:outline-none transition-colors ${accent ? 'border-[#00E5FF]/30 text-[#00E5FF] font-bold focus:border-[#00E5FF]/60' : 'border-white/10 text-white focus:border-[#00E5FF]/50'} ${className}`}
    />
  );
}

function NumInput({ value, onChange, placeholder, accent }) {
  return (
    <Input
      value={value === 0 || value ? String(value) : ''}
      onChange={(v) => onChange(v === '' ? '' : Number(v))}
      placeholder={placeholder}
      accent={accent}
    />
  );
}

function StringList({ items, onChange, placeholder = 'Item…', accent }) {
  const add = () => onChange([...(items || []), '']);
  const rm = (i) => onChange(items.filter((_, idx) => idx !== i));
  const upd = (i, v) => onChange(items.map((it, idx) => idx === i ? v : it));
  return (
    <div className="space-y-1.5">
      {(items || []).map((it, i) => (
        <div key={i} className="flex gap-2">
          <Input value={it} onChange={(v) => upd(i, v)} placeholder={placeholder} accent={accent} />
          <button onClick={() => rm(i)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors flex-shrink-0">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button onClick={add} className="flex items-center gap-1 text-[11px] text-[#00E5FF] hover:text-white transition-colors">
        <Plus className="w-3 h-3" /> Add
      </button>
    </div>
  );
}

/* Generic move helpers */
const move = (arr, i, dir) => {
  const next = [...arr];
  const j = i + dir;
  if (j < 0 || j >= next.length) return arr;
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

/* ---------- Section wrapper ---------- */
function Section({ title, subtitle, count, action, children, defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testId} className="bg-[#111111] border border-white/5 rounded-2xl mb-6 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-6 py-5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} />
        <div className="flex-1 min-w-0">
          <h3 className="font-['Outfit'] text-base font-bold text-white">{title}{typeof count === 'number' && <span className="ml-2 text-xs font-normal text-slate-500">· {count}</span>}</h3>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {action && <div onClick={(e) => e.stopPropagation()}>{action}</div>}
      </button>
      {open && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
}

/* ---------- Reorder controls ---------- */
function MoveBar({ onUp, onDown, onRemove, disableUp, disableDown }) {
  return (
    <div className="flex flex-col items-center gap-1 pt-1">
      <button onClick={onUp} disabled={disableUp} className="text-slate-600 hover:text-white disabled:opacity-20 transition-colors">
        <ChevronUp className="w-4 h-4" />
      </button>
      <button onClick={onDown} disabled={disableDown} className="text-slate-600 hover:text-white disabled:opacity-20 transition-colors">
        <ChevronDown className="w-4 h-4" />
      </button>
      <button onClick={onRemove} className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 transition-colors mt-1">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

/* ===========================================================
   MAIN
=========================================================== */
export default function PricingManager() {
  const [localLaunch, setLocalLaunch] = useState({ name: '', price: '', description: '', features: [] });
  const [tiers, setTiers] = useState([]);
  const [boltOns, setBoltOns] = useState([]);
  const [carePlans, setCarePlans] = useState([]);
  const [hostingTiers, setHostingTiers] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [chips, setChips] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showFloatingSave, setShowFloatingSave] = useState(false);
  const headerSaveRef = useRef(null);

  // Show a floating Save All FAB once the header Save button scrolls off-screen.
  useEffect(() => {
    const onScroll = () => {
      const btn = headerSaveRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setShowFloatingSave(rect.bottom < 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  /* ---- existing legacy fields kept so save doesn't blank them ---- */
  const [legacy, setLegacy] = useState({ packages: [], bolt_ons: [], care_plans_legacy: { feature_labels: [], plans: [] } });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/pricing`);
      const d = await res.json();
      if (d.local_launch?.name) setLocalLaunch(d.local_launch);
      const pb = d.project_builder || {};
      setTiers(pb.tiers || []);
      setBoltOns(pb.bolt_ons || []);
      setCarePlans(pb.care_plans || []);
      setHostingTiers(pb.hosting_tiers || []);
      setWarnings(pb.no_hosting_warnings || []);
      setChips(pb.brief_helper_chips || []);
      setLegacy({
        packages: d.packages || [],
        bolt_ons: d.bolt_ons || [],
        care_plans_legacy: d.care_plans || { feature_labels: [], plans: [] },
      });
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const token = localStorage.getItem('admin_token');
      const project_builder = {
        version: 4,
        tiers,
        bolt_ons: boltOns,
        care_plans: carePlans,
        hosting_tiers: hostingTiers,
        no_hosting_warnings: warnings,
        brief_helper_chips: chips,
      };
      await fetch(`${API}/api/admin/pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          local_launch: localLaunch,
          packages: legacy.packages,
          bolt_ons: legacy.bolt_ons,
          care_plans: legacy.care_plans_legacy,
          project_builder,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    if (!confirm('Reset Project Builder fields (tiers, power-ups, care plans, hosting, brief helpers) to spreadsheet defaults? This will overwrite current values.')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('admin_token');
      // Fetch fresh defaults: trigger by sending project_builder: null and let server re-seed v3 — we do it client-side using a small reset endpoint pattern: just blank version so server backfills.
      // Simpler: just refetch which won't reset, so we hit our reset endpoint
      const res = await fetch(`${API}/api/admin/pricing/reset-builder`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchData();
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        alert('Reset failed — endpoint not available.');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="pricing-manager" className="space-y-6">
        <div className="h-8 w-48 bg-white/5 rounded animate-pulse" />
        <div className="h-64 bg-white/5 rounded-2xl animate-pulse" />
      </div>
    );
  }

  /* ============ UI ============ */
  return (
    <div data-testid="pricing-manager">
      {/* Header bar */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Outfit'] text-xl font-bold text-white">Pricing &amp; Quote Builder</h2>
          <p className="text-sm text-slate-500 mt-0.5">Edit every value that powers <span className="text-[#00E5FF]">/pricing</span> and global SEO references.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleResetDefaults}
            disabled={resetting}
            data-testid="pricing-reset-btn"
            title="Reset Project Builder fields to spreadsheet defaults"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-white/10 text-xs text-slate-400 hover:text-white hover:border-white/20 transition-colors disabled:opacity-50"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${resetting ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Reset</span>
          </button>
          {saved && <span className="text-xs text-emerald-400">Saved</span>}
          <button
            ref={headerSaveRef}
            data-testid="pricing-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save All'}
          </button>
        </div>
      </div>

      {/* Floating Save-All FAB — appears when the header button scrolls off-screen */}
      {showFloatingSave && (
        <button
          data-testid="pricing-save-floating-btn"
          onClick={handleSave}
          disabled={saving}
          className={`fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full text-sm font-semibold shadow-[0_8px_28px_rgba(0,229,255,0.35)] transition-all duration-300 disabled:opacity-60 animate-[fadeIn_0.25s_ease] ${
            saved
              ? 'bg-emerald-400 text-[#0A0A0A]'
              : 'bg-[#00E5FF] text-[#0A0A0A] hover:bg-white hover:shadow-[0_12px_36px_rgba(0,229,255,0.5)] active:scale-[0.97]'
          }`}
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save All'}
        </button>
      )}

      {/* ============ Local Launch ============ */}
      <Section
        title="Local Launch — Global Price"
        subtitle="Used across SEO pages, FAQ, and structured data. Changing this updates the £ everywhere."
        defaultOpen
        testId="local-launch-editor"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <Field label="Name">
            <Input value={localLaunch.name} onChange={(v) => setLocalLaunch(p => ({ ...p, name: v }))} />
          </Field>
          <Field label="Price (£) — global" hint="Updates every page that mentions Local Launch.">
            <Input value={localLaunch.price} onChange={(v) => setLocalLaunch(p => ({ ...p, price: v }))} accent placeholder="399" />
          </Field>
        </div>
        <Field label="Description">
          <textarea
            value={localLaunch.description || ''}
            onChange={(e) => setLocalLaunch(p => ({ ...p, description: e.target.value }))}
            rows={2}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 resize-none"
          />
        </Field>
        <div className="mt-4">
          <Field label="Features (used on SEO pages)">
            <StringList items={localLaunch.features} onChange={(f) => setLocalLaunch(p => ({ ...p, features: f }))} placeholder="Feature…" />
          </Field>
        </div>
      </Section>

      {/* ============ Foundation Tiers ============ */}
      <Section
        title="Foundation Tiers"
        subtitle="Step 1 of the Quote Builder. Cards shown on /pricing."
        count={tiers.length}
        testId="tiers-editor"
        action={
          <button
            onClick={() => setTiers([...tiers, { id: `tier_${Date.now()}`, name: 'New tier', price: 0, tagline: '', description: '', deliverables: [] }])}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00E5FF] hover:text-white transition-colors bg-[#00E5FF]/10 px-3 py-1.5 rounded-lg"
          >
            <Plus className="w-3 h-3" /> Add tier
          </button>
        }
      >
        <div className="space-y-4">
          {tiers.map((t, i) => (
            <div key={t.id || i} data-testid={`tier-editor-${i}`} className="flex items-start gap-3 bg-white/[0.03] border border-white/5 rounded-xl p-4">
              <MoveBar
                onUp={() => setTiers(prev => move(prev, i, -1))}
                onDown={() => setTiers(prev => move(prev, i, 1))}
                onRemove={() => setTiers(prev => prev.filter((_, idx) => idx !== i))}
                disableUp={i === 0}
                disableDown={i === tiers.length - 1}
              />
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="ID (slug)"><Input value={t.id} onChange={(v) => setTiers(p => p.map((x, idx) => idx === i ? { ...x, id: v } : x))} placeholder="growth" /></Field>
                <Field label="Name"><Input value={t.name} onChange={(v) => setTiers(p => p.map((x, idx) => idx === i ? { ...x, name: v } : x))} /></Field>
                <Field label="Price (£)"><NumInput value={t.price} onChange={(v) => setTiers(p => p.map((x, idx) => idx === i ? { ...x, price: v } : x))} placeholder="800" accent /></Field>
                <Field label="Tagline" full><Input value={t.tagline} onChange={(v) => setTiers(p => p.map((x, idx) => idx === i ? { ...x, tagline: v } : x))} placeholder="One-page digital business card" /></Field>
                <Field label="Pages chip"><Input value={t.pages} onChange={(v) => setTiers(p => p.map((x, idx) => idx === i ? { ...x, pages: v } : x))} placeholder="3 pages" /></Field>
                <Field label="Revisions chip"><Input value={t.revisions} onChange={(v) => setTiers(p => p.map((x, idx) => idx === i ? { ...x, revisions: v } : x))} placeholder="2 revisions" /></Field>
                <Field label="Description" full>
                  <textarea
                    value={t.description || ''}
                    onChange={(e) => setTiers(p => p.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 resize-none"
                  />
                </Field>
                <Field label="Deliverables" full>
                  <StringList
                    items={t.deliverables}
                    onChange={(d) => setTiers(p => p.map((x, idx) => idx === i ? { ...x, deliverables: d } : x))}
                    placeholder="3 custom-designed pages"
                  />
                </Field>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ Bolt-ons / Power-ups ============ */}
      <Section
        title="Power-ups (Bolt-ons)"
        subtitle="Step 2 of the Quote Builder. Multi-select grid."
        count={boltOns.length}
        testId="boltons-editor"
        action={
          <button
            onClick={() => setBoltOns([...boltOns, { id: `bolt_${Date.now()}`, name: 'New bolt-on', tagline: '', price: 0, price_suffix: '', icon: 'Sparkles', description: '', difficulty: 'Easy' }])}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00E5FF] hover:text-white transition-colors bg-[#00E5FF]/10 px-3 py-1.5 rounded-lg"
          >
            <Plus className="w-3 h-3" /> Add bolt-on
          </button>
        }
      >
        <div className="space-y-3">
          {boltOns.map((b, i) => {
            const Icon = ICON_LOOKUP[b.icon] || Sparkles;
            return (
              <div key={b.id || i} data-testid={`bolton-editor-${i}`} className="flex items-start gap-3 bg-white/[0.03] border border-white/5 rounded-xl p-4">
                <MoveBar
                  onUp={() => setBoltOns(prev => move(prev, i, -1))}
                  onDown={() => setBoltOns(prev => move(prev, i, 1))}
                  onRemove={() => setBoltOns(prev => prev.filter((_, idx) => idx !== i))}
                  disableUp={i === 0}
                  disableDown={i === boltOns.length - 1}
                />
                <div className="w-9 h-9 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon className="w-4 h-4 text-[#00E5FF]" />
                </div>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-6 gap-3">
                  <Field label="ID"><Input value={b.id} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, id: v } : x))} placeholder="logo_branding" /></Field>
                  <Field label="Name"><Input value={b.name} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, name: v } : x))} /></Field>
                  <Field label="Tagline (small label)"><Input value={b.tagline} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, tagline: v } : x))} placeholder="The Trust Multiplier" /></Field>
                  <Field label="Price (£)"><NumInput value={b.price} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, price: v } : x))} placeholder="600" accent /></Field>
                  <Field label="+ suffix"><Input value={b.price_suffix} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, price_suffix: v } : x))} placeholder="+" /></Field>
                  <Field label="Icon">
                    <select
                      value={b.icon || 'Sparkles'}
                      onChange={(e) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, icon: e.target.value } : x))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50"
                    >
                      {ICON_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Description" full>
                    <textarea
                      value={b.description || ''}
                      onChange={(e) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
                      rows={2}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 resize-none"
                    />
                  </Field>
                  <Field label="Difficulty (internal)">
                    <select
                      value={b.difficulty || 'Easy'}
                      onChange={(e) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, difficulty: e.target.value } : x))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50"
                    >
                      <option>Easy</option>
                      <option>Medium</option>
                      <option>Hard</option>
                    </select>
                  </Field>

                  {/* Front-end behaviour flags */}
                  <Field label="Front-end placement" full>
                    <div className="flex flex-wrap gap-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-slate-300 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 hover:border-amber-400/40 transition-colors">
                        <input
                          type="checkbox"
                          data-testid={`bolton-requires-care-${i}`}
                          checked={!!b.requires_care}
                          onChange={(e) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, requires_care: e.target.checked } : x))}
                          className="accent-amber-400 cursor-pointer"
                        />
                        <span><span className="font-semibold text-amber-300">Requires Care Plan</span> — shown in lower section, forces a Care Plan in Step 4</span>
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-slate-300 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 hover:border-violet-400/40 transition-colors">
                        <input
                          type="checkbox"
                          data-testid={`bolton-has-boost-${i}`}
                          checked={!!b.has_boost}
                          onChange={(e) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, has_boost: e.target.checked, boost_options: x.boost_options?.length ? x.boost_options : (e.target.checked ? [{ id: 'starter', name: 'Starter Pack', price_monthly: 10, credits: '1,000 Credits', interactions: '~300–800 interactions', description: '' }] : []) } : x))}
                          className="accent-violet-400 cursor-pointer"
                        />
                        <span><span className="font-semibold text-violet-300">Has boost slider</span> — adds a monthly credit-pack picker on the front-end card</span>
                      </label>
                    </div>
                  </Field>

                  {/* Boost packs editor — visible only when has_boost is on */}
                  {b.has_boost && (
                    <Field label={`Boost packs · ${b.boost_options?.length || 0}`} full>
                      <div className="space-y-2 mb-2">
                        {(b.boost_options || []).map((bp, j) => (
                          <div key={bp.id || j} data-testid={`bolton-${i}-boost-${j}`} className="grid grid-cols-1 sm:grid-cols-12 gap-2 bg-white/[0.02] border border-violet-500/20 rounded-lg p-2.5">
                            <div className="sm:col-span-2"><Input value={bp.id} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, boost_options: x.boost_options.map((bo, bj) => bj === j ? { ...bo, id: v } : bo) } : x))} placeholder="starter" /></div>
                            <div className="sm:col-span-3"><Input value={bp.name} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, boost_options: x.boost_options.map((bo, bj) => bj === j ? { ...bo, name: v } : bo) } : x))} placeholder="Starter Pack" /></div>
                            <div className="sm:col-span-2"><NumInput value={bp.price_monthly} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, boost_options: x.boost_options.map((bo, bj) => bj === j ? { ...bo, price_monthly: v } : bo) } : x))} placeholder="10" accent /></div>
                            <div className="sm:col-span-2"><Input value={bp.credits} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, boost_options: x.boost_options.map((bo, bj) => bj === j ? { ...bo, credits: v } : bo) } : x))} placeholder="1,000 Credits" /></div>
                            <div className="sm:col-span-2"><Input value={bp.interactions} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, boost_options: x.boost_options.map((bo, bj) => bj === j ? { ...bo, interactions: v } : bo) } : x))} placeholder="~300–800 interactions" /></div>
                            <div className="sm:col-span-1 flex items-center justify-end">
                              <button
                                type="button"
                                onClick={() => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, boost_options: x.boost_options.filter((_, bj) => bj !== j) } : x))}
                                className="w-7 h-7 rounded-md flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                                title="Remove pack"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div className="sm:col-span-12"><Input value={bp.description} onChange={(v) => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, boost_options: x.boost_options.map((bo, bj) => bj === j ? { ...bo, description: v } : bo) } : x))} placeholder="Short pack description (shown on front-end)" /></div>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setBoltOns(p => p.map((x, idx) => idx === i ? { ...x, boost_options: [...(x.boost_options || []), { id: `pack_${Date.now()}`, name: 'New pack', price_monthly: 0, credits: '', interactions: '', description: '' }] } : x))}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-300 hover:text-white transition-colors bg-violet-500/10 px-2.5 py-1.5 rounded-md"
                      >
                        <Plus className="w-3 h-3" /> Add boost pack
                      </button>
                    </Field>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ============ Care Plans ============ */}
      <Section
        title="Care Plans"
        subtitle="Step 4. Recurring monthly subscription tiers."
        count={carePlans.length}
        testId="careplans-editor"
        action={
          <button
            onClick={() => setCarePlans([...carePlans, { id: `care_${Date.now()}`, name: 'New plan', tagline: '', price_monthly: 0, price_suffix: '', features: [], popular: false }])}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00E5FF] hover:text-white transition-colors bg-[#00E5FF]/10 px-3 py-1.5 rounded-lg"
          >
            <Plus className="w-3 h-3" /> Add plan
          </button>
        }
      >
        <div className="space-y-3">
          {carePlans.map((c, i) => (
            <div key={c.id || i} data-testid={`careplan-editor-${i}`} className="flex items-start gap-3 bg-white/[0.03] border border-white/5 rounded-xl p-4">
              <MoveBar
                onUp={() => setCarePlans(prev => move(prev, i, -1))}
                onDown={() => setCarePlans(prev => move(prev, i, 1))}
                onRemove={() => setCarePlans(prev => prev.filter((_, idx) => idx !== i))}
                disableUp={i === 0}
                disableDown={i === carePlans.length - 1}
              />
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-5 gap-3">
                <Field label="ID"><Input value={c.id} onChange={(v) => setCarePlans(p => p.map((x, idx) => idx === i ? { ...x, id: v } : x))} placeholder="growth" /></Field>
                <Field label="Name"><Input value={c.name} onChange={(v) => setCarePlans(p => p.map((x, idx) => idx === i ? { ...x, name: v } : x))} /></Field>
                <Field label="Tagline"><Input value={c.tagline} onChange={(v) => setCarePlans(p => p.map((x, idx) => idx === i ? { ...x, tagline: v } : x))} placeholder="The Performer" /></Field>
                <Field label="£/month"><NumInput value={c.price_monthly} onChange={(v) => setCarePlans(p => p.map((x, idx) => idx === i ? { ...x, price_monthly: v } : x))} placeholder="150" accent /></Field>
                <Field label="+ suffix"><Input value={c.price_suffix} onChange={(v) => setCarePlans(p => p.map((x, idx) => idx === i ? { ...x, price_suffix: v } : x))} placeholder="+" /></Field>
                <Field label="Features" full>
                  <StringList
                    items={c.features}
                    onChange={(f) => setCarePlans(p => p.map((x, idx) => idx === i ? { ...x, features: f } : x))}
                    placeholder="24-hour priority support"
                  />
                </Field>
                <Field label="Most popular?">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!c.popular}
                      onChange={(e) => setCarePlans(p => p.map((x, idx) => idx === i ? { ...x, popular: e.target.checked } : x))}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 accent-[#00E5FF]"
                    />
                    <span className="text-xs text-slate-300">Highlight as most popular</span>
                  </label>
                </Field>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ Hosting Tiers ============ */}
      <Section
        title="Hosting Tiers (Slider)"
        subtitle="Step 4 hosting slider. Starter is the default free option."
        count={hostingTiers.length}
        testId="hosting-editor"
        action={
          <button
            onClick={() => setHostingTiers([...hostingTiers, { id: `host_${Date.now()}`, name: 'New tier', ram: '', monthly_surcharge: 0, description: '' }])}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00E5FF] hover:text-white transition-colors bg-[#00E5FF]/10 px-3 py-1.5 rounded-lg"
          >
            <Plus className="w-3 h-3" /> Add tier
          </button>
        }
      >
        <div className="space-y-3">
          {hostingTiers.map((h, i) => (
            <div key={h.id || i} data-testid={`hosting-editor-${i}`} className="flex items-start gap-3 bg-white/[0.03] border border-white/5 rounded-xl p-4">
              <MoveBar
                onUp={() => setHostingTiers(prev => move(prev, i, -1))}
                onDown={() => setHostingTiers(prev => move(prev, i, 1))}
                onRemove={() => setHostingTiers(prev => prev.filter((_, idx) => idx !== i))}
                disableUp={i === 0}
                disableDown={i === hostingTiers.length - 1}
              />
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-3">
                <Field label="ID"><Input value={h.id} onChange={(v) => setHostingTiers(p => p.map((x, idx) => idx === i ? { ...x, id: v } : x))} /></Field>
                <Field label="Name"><Input value={h.name} onChange={(v) => setHostingTiers(p => p.map((x, idx) => idx === i ? { ...x, name: v } : x))} /></Field>
                <Field label="RAM"><Input value={h.ram} onChange={(v) => setHostingTiers(p => p.map((x, idx) => idx === i ? { ...x, ram: v } : x))} placeholder="2 GB" /></Field>
                <Field label="+£/month"><NumInput value={h.monthly_surcharge} onChange={(v) => setHostingTiers(p => p.map((x, idx) => idx === i ? { ...x, monthly_surcharge: v } : x))} placeholder="29.99" accent /></Field>
                <Field label="Description" full>
                  <textarea
                    value={h.description || ''}
                    onChange={(e) => setHostingTiers(p => p.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 resize-none"
                  />
                </Field>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ Warnings & Chips ============ */}
      <Section
        title="No-hosting Warnings"
        subtitle="Shown when user opts out of managed hosting."
        count={warnings.length}
        defaultOpen={false}
        testId="warnings-editor"
      >
        <StringList items={warnings} onChange={setWarnings} placeholder="You lose access to your custom admin panel" />
      </Section>

      <Section
        title="Brief Helper Chips"
        subtitle="Step 3. Tappable suggestions that fill the project brief."
        count={chips.length}
        defaultOpen={false}
        testId="chips-editor"
      >
        <StringList items={chips} onChange={setChips} placeholder="I need to take online payments" />
      </Section>
    </div>
  );
}
